import { DERRIBO_DURATION_S, SIM_HZ } from '../data/tuning';
import { applyGameOver, retry } from './game-over';
import type { LevelRun } from './level-runner';
import { startInvulnerability } from './lives';
import { respawn } from './respawn';
import type { CoreEventInput, TickContext } from './tick';

/**
 * Level state machine (GDD §13.2, §6.2 rules 4, 6 and 7). Pause is a flag of the loop (#26), not a
 * state.
 *
 *            FLAP              CRASH             RESPAWN (lives > 0)
 *   INTRO ---------> VUELO ----------> DERRIBO ---------------------> REAPARICION
 *     ^                |  ^                |                               |
 *     |      LEVEL_END |  +----------------|-------------------------------+
 *     |                v                   | FAIL (lives = 0)          FLAP
 *     |           COMPLETADO               v
 *     +------------------------------ OPERACION_FALLIDA
 *                  RETRY
 *
 *   VUELO --CORRIDOR_OPEN--> CIELO_EEUU --JUMP_ZONE--> SALTO --LEVEL_END--> COMPLETADO   (El Salto, #92, #93)
 *
 * - INTRO and REAPARICION hold the plane still (no gravity, no scroll) until the first flap, which also
 *   applies the impulse. Leaving REAPARICION starts the 2.0 s invulnerability.
 * - DERRIBO lasts 1.5 s with the scroll stopped and no pickups nor collisions; its end fires RESPAWN or
 *   FAIL depending on the lives left.
 * - Only VUELO resolves pickups, triggers and damaging collisions; flaps count in INTRO, VUELO and
 *   REAPARICION.
 */
export type LevelState =
  | 'INTRO'
  | 'VUELO'
  | 'DERRIBO'
  | 'REAPARICION'
  | 'OPERACION_FALLIDA'
  | 'COMPLETADO'
  | 'CIELO_EEUU'
  | 'SALTO';
export type LevelEvent = 'FLAP' | 'CRASH' | 'RESPAWN' | 'FAIL' | 'LEVEL_END' | 'RETRY' | 'CORRIDOR_OPEN' | 'JUMP_ZONE';

export const LEVEL_STATES: readonly LevelState[] = [
  'INTRO',
  'VUELO',
  'DERRIBO',
  'REAPARICION',
  'OPERACION_FALLIDA',
  'COMPLETADO',
  'CIELO_EEUU',
  'SALTO',
];
export const LEVEL_EVENTS: readonly LevelEvent[] = ['FLAP', 'CRASH', 'RESPAWN', 'FAIL', 'LEVEL_END', 'RETRY', 'CORRIDOR_OPEN', 'JUMP_ZONE'];

/** The only valid transitions; everything else is rejected. */
export const TRANSITIONS: Readonly<Partial<Record<LevelState, Partial<Record<LevelEvent, LevelState>>>>> = {
  INTRO: { FLAP: 'VUELO' },
  VUELO: { CRASH: 'DERRIBO', LEVEL_END: 'COMPLETADO', CORRIDOR_OPEN: 'CIELO_EEUU' },
  // El Salto (GDD §11.1, issue #92): the US sky after the boss, then the jump zone; the jump itself is #93.
  CIELO_EEUU: { JUMP_ZONE: 'SALTO' },
  // The landing of El Salto completes the level (#93).
  SALTO: { LEVEL_END: 'COMPLETADO' },
  DERRIBO: { RESPAWN: 'REAPARICION', FAIL: 'OPERACION_FALLIDA' },
  REAPARICION: { FLAP: 'VUELO' },
  OPERACION_FALLIDA: { RETRY: 'INTRO' },
};

export const DERRIBO_TICKS = Math.round(DERRIBO_DURATION_S * SIM_HZ);

/** States where the plane waits still for the first flap. */
export const isHolding = (state: string): boolean => state === 'INTRO' || state === 'REAPARICION';

/** States where flaps count: in the jump zone the autopilot flies (GDD §11.1). */
export const acceptsFlap = (state: string): boolean => state === 'VUELO' || state === 'CIELO_EEUU' || isHolding(state);

/** States where the world scrolls: the flight, the US sky, the jump zone and past the finish. */
export const scrolls = (state: string): boolean =>
  state === 'VUELO' || state === 'COMPLETADO' || state === 'CIELO_EEUU' || state === 'SALTO';

export function nextState(state: LevelState, event: LevelEvent): LevelState | null {
  return TRANSITIONS[state]?.[event] ?? null;
}

/**
 * Applies `event` to the run. Returns false (no exception) for an invalid transition. Inside a tick the
 * events go through `context`; outside (the RETRY of the screen, test drivers) they are dispatched to the
 * listeners right away.
 */
export function transition(run: LevelRun, event: LevelEvent, context?: TickContext): boolean {
  const { core } = run;
  const from = core.levelState as LevelState;
  const to = nextState(from, event);
  if (!to) {
    if (import.meta.env?.DEV) console.warn(`level state: ${event} rejected in ${from}`);
    return false;
  }
  const emit = (input: CoreEventInput) => {
    if (context) context.emit(input);
    else for (const listener of core.listeners) listener({ ...input, tick: core.tick }, core);
  };

  if (event === 'RESPAWN') respawn(run);
  if (event === 'RETRY') retry(run);
  core.levelState = to;
  run.observedState = to;
  if (to === 'DERRIBO') run.derriboRemaining = DERRIBO_TICKS;
  if (from === 'REAPARICION' && to === 'VUELO') startInvulnerability(core);
  if (event === 'FAIL') emit(applyGameOver(run));
  emit({ type: 'LEVEL_STATE_CHANGED', from, to });
  return true;
}
